import { useEffect, useRef } from 'react';

import type { AnchorInfo } from '@aline/common';
import { getReviewableSuggestions } from '@aline/common';

import { parseProposal } from './playbook-run-hooks';
import type { PlaybookProposal, PlaybookRuleJobSummary } from './playbook-run-state';
import {
  getIsRuleRun,
  getIsRuleRunFailed,
  getIsRuleRunning,
  getRuleJob,
} from './playbook-run-state';
import { getIsSuggestionStale } from './playbook-staleness';

/**
 * How long the document must stay quiet before stale rules rerun. Content
 * ticks reset the timer, so mid-typing edits never fire a rerun against a
 * half-written sentence.
 */
const QUIET_PERIOD_MS = 2000;

/**
 * The slice of the run data the qualification reads — a summary job whose
 * proposals are the full records (the summary's own slim, decisions-only
 * proposals cannot say whether a suggestion is stale).
 */
export type AutoRerunRule = {
  id: string;
  ruleJob?: (Omit<PlaybookRuleJobSummary, 'proposals'> & { proposals: PlaybookProposal[] }) | null;
};
export type AutoRerunCategory = { rules: AutoRerunRule[] };

/**
 * The rules auto-rerun would refresh right now: unresolved, finished,
 * not-running rules whose proposal has gone stale against the live document.
 *
 * Never included:
 * - the rule being worked in — a resolved rule computes no staleness, and a
 *   mid-review rule is excluded because a rerun destroys its proposals and
 *   decisions; its stale banner shows instead. Mid-review counts recorded
 *   decisions AND accepts still in flight
 *   (`pendingAcceptedSuggestionIds`) — an accept changes the document before
 *   its decision lands, and that window must never read as "stale rule with
 *   no review underway";
 * - running, failed, or never-run rules — there is nothing fresh to replace;
 * - rules in `skipRuleIds` (reruns already in flight).
 */
export function getAutoRerunRuleIds(options: {
  anchorInfoByKey: Map<string, AnchorInfo>;
  categories: AutoRerunCategory[];
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  skipRuleIds: ReadonlySet<string>;
}): string[] {
  const { anchorInfoByKey, categories, pendingAcceptedSuggestionIds, skipRuleIds } = options;
  const ruleIds: string[] = [];
  for (const category of categories) {
    for (const rule of category.rules) {
      const job = getRuleJob(rule);
      if (!job || !getIsRuleRun(job) || getIsRuleRunning(job) || getIsRuleRunFailed(job)) continue;
      if (job.resolution) continue;
      if (job.proposals.length === 0) continue;
      if (skipRuleIds.has(rule.id)) continue;

      const proposals = job.proposals.flatMap(stored => {
        const parsed = parseProposal(stored);
        return parsed ? [parsed] : [];
      });
      const isMidReview = proposals.some(
        proposal =>
          Object.keys(proposal.decisions).length > 0 ||
          proposal.suggestions.some(suggestion => pendingAcceptedSuggestionIds.has(suggestion.id))
      );
      if (isMidReview) continue;

      const isStale = proposals.some(proposal =>
        getReviewableSuggestions(proposal.suggestions).some(suggestion =>
          getIsSuggestionStale(suggestion.previewCards, anchorInfoByKey)
        )
      );
      if (isStale) ruleIds.push(rule.id);
    }
  }
  return ruleIds;
}

/**
 * Auto-rerun: any rule whose proposal has gone stale against the live
 * document is rerun automatically once the document goes quiet — whatever
 * caused the change (typing, chat edits, another rule's accepted suggestion).
 *
 * Evaluation is derivation-driven, not event-driven: it re-judges every rule
 * whenever the document or run data changes, which also refreshes rules that
 * were already stale when the panel opened. A rerun regenerates proposals
 * against the current document and never edits it, so one rerun cannot
 * cascade into another. What qualifies — and what is never touched — is
 * `getAutoRerunRuleIds`.
 */
export function usePlaybookAutoRerun({
  anchorInfoByKey,
  categories,
  editorLoading,
  pendingAcceptedSuggestionIds,
  rerunRules,
}: {
  anchorInfoByKey: Map<string, AnchorInfo>;
  categories: AutoRerunCategory[];
  editorLoading: boolean;
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  rerunRules: (ruleIds: string[]) => Promise<unknown>;
}): void {
  /**
   * Rules whose rerun mutation is in flight. The cache marks the job running
   * only once the mutation responds; without this, every evaluation in that
   * window would start the same rule again.
   */
  const inFlightRuleIds = useRef(new Set<string>());

  useEffect(() => {
    /**
     * Placeholder editor children produce a non-empty map of the wrong
     * document. Wait until Yjs has loaded (and the panel has extracted real
     * anchors) before judging staleness.
     */
    if (editorLoading || anchorInfoByKey.size === 0) return;

    const timer = window.setTimeout(() => {
      const ruleIds = getAutoRerunRuleIds({
        anchorInfoByKey,
        categories,
        pendingAcceptedSuggestionIds,
        skipRuleIds: inFlightRuleIds.current,
      });
      if (!ruleIds.length) return;

      for (const ruleId of ruleIds) inFlightRuleIds.current.add(ruleId);
      void rerunRules(ruleIds)
        /**
         * A concurrent start ("already running") loses harmlessly — the
         * card shows whatever state the winner produced. The whole batch
         * is one mutation, so a loss releases every id in `finally`.
         */
        .catch(() => undefined)
        .finally(() => {
          for (const ruleId of ruleIds) inFlightRuleIds.current.delete(ruleId);
        });
    }, QUIET_PERIOD_MS);

    return () => window.clearTimeout(timer);
  }, [anchorInfoByKey, categories, editorLoading, pendingAcceptedSuggestionIds, rerunRules]);
}
